/**
 * API client for ConcoursCI backend.
 * Handles all HTTP requests to the FastAPI backend.
 * Version 2.0 - Full Supabase integration
 */
import axios, { AxiosInstance } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/** fetch wrapper that automatically attaches the JWT token */
export function authFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem('concoursci_token');
  return fetch(input, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

// Types
export interface Exam {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  background_url?: string;
  is_active: boolean;
}

export interface Subject {
  id: string;
  name: string;
  description?: string;
  exam_id?: string;
  order?: number;
  difficulty?: string;
}

export interface Quiz {
  id: string;
  title: string;
  description?: string;
  subject_id: string;
  exam_id: string;
  question_count?: number;
  estimated_time?: number;
  difficulty?: string;
  order?: number;
}

export interface Question {
  id: string;
  quiz_id: string;
  subject_id: string;
  exam_id: string;
  type: string;
  text: string;
  options?: any;
  correct_answer: string;
  explanation: string;
  difficulty: string;
  order: number;
  hint?: string;
  topic?: string;
  blank_sentence?: string;
  blank_options?: string[];
  blank_correct?: string;
  extra_data?: any;
}

export interface CourseData {
  quiz_id: string;
  subject: string;
  detected_subject: string;
  topic: string;
  questions: Question[];
  total_levels: number;
  metadata: {
    exam_id?: string;
    subject_id?: string;
    quiz_id?: string;
    quiz_title?: string;
    quiz_description?: string;
    subject_name?: string;
    [key: string]: any;
  };
}

export interface UploadResult {
  success: boolean;
  inserted_count?: number;
  quiz_ids?: string[];
  message: string;
  error?: string;
}

// All 13 Question Types
export type QuestionType =
  | 'mcq'              // 1. Questions à Choix Multiple
  | 'fillBlank'        // 2. Texte à Trous
  | 'correction'       // 3. Correction de Phrase
  | 'sequence'         // 4. Suite Logique
  | 'caseStudy'        // 5. Cas Pratique
  | 'flashcard'        // 6. Carte Mémoire
  | 'matching'         // 7. Association
  | 'ordering'         // 8. Classement
  | 'audioDictation'   // 9. Dictée Audio
  | 'speedChallenge'   // 10. Défi Chrono
  | 'adaptive'         // 11. Test Adaptatif
  | 'interview'        // 12. Entretien Oral
  | 'essay';           // 13. Rédaction/Dissertation

export type DifficultyLevel = 'easy' | 'medium' | 'hard';

export interface QuestionTypeSpec {
  name: string;
  description: string;
  options_format: string;
  correct_answer_format: string;
}

export interface SingleQuestionRequest {
  topic: string;
  question_type: QuestionType;
  difficulty?: DifficultyLevel;
  subject?: string;
  image_base64?: string;
  context?: string;
  exam_id?: string;
  subject_id?: string;
  quiz_id?: string;
}

export interface SingleQuestionResponse {
  question: Question;
  generation_time: number;
  metadata: {
    topic: string;
    question_type: string;
    difficulty: string;
    type_spec: QuestionTypeSpec;
  };
}

// Question type display info for UI
export const QUESTION_TYPE_INFO: Record<QuestionType, { name: string; icon: string; description: string }> = {
  mcq: { name: 'QCM', icon: '☑️', description: 'Questions à choix multiple' },
  fillBlank: { name: 'Texte à trous', icon: '✏️', description: 'Compléter les blancs' },
  correction: { name: 'Correction', icon: '🔧', description: 'Corriger les erreurs' },
  sequence: { name: 'Suite logique', icon: '🔢', description: 'Trouver le suivant' },
  caseStudy: { name: 'Cas pratique', icon: '📋', description: 'Situation réelle' },
  flashcard: { name: 'Flashcard', icon: '🃏', description: 'Carte mémoire' },
  matching: { name: 'Association', icon: '🔗', description: 'Relier les éléments' },
  ordering: { name: 'Classement', icon: '📊', description: 'Mettre en ordre' },
  audioDictation: { name: 'Dictée', icon: '🎧', description: 'Écouter et écrire' },
  speedChallenge: { name: 'Défi chrono', icon: '⏱️', description: 'Réponses rapides' },
  adaptive: { name: 'Adaptatif', icon: '🎯', description: 'Difficulté adaptée' },
  interview: { name: 'Entretien', icon: '🎤', description: 'Question orale' },
  essay: { name: 'Dissertation', icon: '📝', description: 'Rédaction longue' }
};

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 300000, // 5 minutes for GPT-4.1 Vision processing
    });

    // Attach JWT token to every request
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('concoursci_token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    });
  }

  /**
   * Health check
   */
  async healthCheck() {
    const response = await this.client.get('/health');
    return response.data;
  }

  // ========================================
  // SUPABASE DATA ENDPOINTS
  // ========================================

  /**
   * Get list of exams (concours) from Supabase
   */
  async getExams(activeOnly: boolean = true): Promise<{ exams: Exam[]; count: number }> {
    const response = await this.client.get('/api/exams', {
      params: { active_only: activeOnly }
    });
    return response.data;
  }

  /**
   * Get list of subjects (matières)
   * If examId is provided, fetches from Supabase; otherwise returns local config
   */
  async getSubjects(examId?: string): Promise<{ subjects: Subject[]; source: string }> {
    const response = await this.client.get('/api/subjects', {
      params: examId ? { exam_id: examId } : {}
    });
    return response.data;
  }

  /**
   * Get list of quizzes from Supabase
   */
  async getQuizzes(examId?: string, subjectId?: string): Promise<{ quizzes: Quiz[]; count: number }> {
    const response = await this.client.get('/api/quizzes', {
      params: {
        ...(examId && { exam_id: examId }),
        ...(subjectId && { subject_id: subjectId })
      }
    });
    return response.data;
  }

  // ========================================
  // COURSE GENERATION
  // ========================================

  /**
   * Generate course from image file with optional exam/subject targeting
   */
  async generateCourseFromFile(
    file: File,
    options?: {
      subject?: string;
      examId?: string;
      subjectId?: string;
      quizId?: string;
    }
  ): Promise<CourseData> {
    const formData = new FormData();
    formData.append('file', file);

    if (options?.subject) formData.append('subject', options.subject);
    if (options?.examId) formData.append('exam_id', options.examId);
    if (options?.subjectId) formData.append('subject_id', options.subjectId);
    if (options?.quizId) formData.append('quiz_id', options.quizId);

    const response = await this.client.post('/api/generate-course', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  /**
   * Generate course from base64 image
   */
  async generateCourseFromBase64(
    imageBase64: string,
    options?: {
      subject?: string;
      examId?: string;
      subjectId?: string;
      quizId?: string;
    }
  ): Promise<CourseData> {
    const formData = new FormData();
    formData.append('image_base64', imageBase64);

    if (options?.subject) formData.append('subject', options.subject);
    if (options?.examId) formData.append('exam_id', options.examId);
    if (options?.subjectId) formData.append('subject_id', options.subjectId);
    if (options?.quizId) formData.append('quiz_id', options.quizId);

    const response = await this.client.post('/api/generate-course', formData);
    return response.data;
  }

  // ========================================
  // UPLOAD TO SUPABASE
  // ========================================

  /**
   * Upload validated questions to Supabase
   */
  async uploadQuestions(
    questions: Question[],
    examId: string,
    subjectId: string,
    quizId: string
  ): Promise<UploadResult> {
    const response = await this.client.post('/api/upload-questions', {
      questions,
      exam_id: examId,
      subject_id: subjectId,
      quiz_id: quizId
    });
    return response.data;
  }

  /**
   * Delete questions from Supabase
   */
  async deleteQuestions(questionIds: string[]): Promise<{ success: boolean; deleted_count: number }> {
    const response = await this.client.delete('/api/questions', {
      data: questionIds
    });
    return response.data;
  }

  // ========================================
  // EXPORT
  // ========================================

  /**
   * Export course to JSON format
   */
  async exportToJson(courseData: CourseData) {
    const response = await this.client.post('/api/export/json', courseData);
    return response.data;
  }

  /**
   * Export course to SQL format
   */
  async exportToSql(courseData: CourseData) {
    const response = await this.client.post('/api/export/sql', courseData);
    return response.data;
  }

  // ========================================
  // QUESTION TYPES & SINGLE QUESTION GENERATION
  // ========================================

  /**
   * Get all supported question types with specifications
   */
  async getQuestionTypes(): Promise<{
    types: Record<string, QuestionTypeSpec>;
    count: number;
    available: string[];
  }> {
    const response = await this.client.get('/api/question-types');
    return response.data;
  }

  /**
   * Generate a single question of a specific type
   */
  async generateSingleQuestion(request: SingleQuestionRequest): Promise<SingleQuestionResponse> {
    const response = await this.client.post('/api/generate-question', request);
    return response.data;
  }
}

export const apiClient = new ApiClient();
export default apiClient;
